import { API_URL, getApiConnectionErrorMessage } from './configAndUrls.js'
import { getUserFriendlyApiErrorMessage } from '../apiErrors.js'
import { ADMIN_TOKEN_BACKUP_KEY, readAdminTokenBackup } from '../impersonationStorage.js'

/** Current tenant slug for X-Tenant header (set by TenantContext). */
let tenantSlug = null

export function getTenantSlug() {
  return tenantSlug
}

export function setTenantSlug(slug) {
  tenantSlug = slug ? String(slug).trim() || null : null
}

const pendingRequests = new Map()
const isMutationMethod = (method) => ['POST', 'PUT', 'PATCH', 'DELETE'].includes(String(method || 'GET').toUpperCase())

const createRequestId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID()
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`
}

export const createMutationIdempotencyKey = (action = 'mutation') => `${action}-${createRequestId()}`

const getToken = () => localStorage.getItem('auth_token')

export const getAuthHeaders = () => ({
  ...(getToken() && { Authorization: `Bearer ${getToken()}` }),
  ...(tenantSlug && { 'X-Tenant': tenantSlug }),
})

export const setAuthToken = (token) => {
  if (token) localStorage.setItem('auth_token', token)
  else localStorage.removeItem('auth_token')
}

const getCacheKey = (endpoint, options = {}) => {
  const method = options.method || 'GET'
  const body = options.body ? JSON.stringify(options.body) : ''
  return `${method}:${endpoint}:${body}`
}

const isAbortError = (error) =>
  error?.name === 'AbortError' || (error instanceof DOMException && error.name === 'AbortError')

const isExpectedLoginFailure = (endpoint, error) => {
  if (endpoint !== '/auth/login') return false
  if (error?.status === 401) return true
  const msg = String(error?.message || '').toLowerCase()
  return msg.includes('unauthorized') || msg.includes('invalid credentials')
}

export const isInfluencerRosterDuplicateError = (error) => {
  const endpoint = String(error?.endpoint || '')
  const msg = String(error?.message || '').toLowerCase()
  return (
    error?.status === 409
    && (error?.code === 'INFLUENCER_ROSTER_DUPLICATE'
      || endpoint === '/influencers/roster'
      || msg.includes('already exists in roster for this tenant'))
  )
}

export const influencerRosterDuplicateMessage =
  'That influencer already exists in your creator roster. Open Influencers to update the existing profile instead.'

export const apiRequest = async (endpoint, options = {}) => {
  const token = getToken()
  const url = `${API_URL}${endpoint}`
  const cacheKey = getCacheKey(endpoint, options)

  if (pendingRequests.has(cacheKey)) {
    const existingPromise = pendingRequests.get(cacheKey)
    if (options.signal?.aborted) {
      throw new DOMException('Aborted', 'AbortError')
    }
    if (options.signal) {
      return new Promise((resolve, reject) => {
        const abortHandler = () => {
          options.signal.removeEventListener('abort', abortHandler)
          reject(new DOMException('Aborted', 'AbortError'))
        }
        options.signal.addEventListener('abort', abortHandler)
        existingPromise
          .then((result) => {
            options.signal.removeEventListener('abort', abortHandler)
            resolve(result)
          })
          .catch((error) => {
            options.signal.removeEventListener('abort', abortHandler)
            reject(error)
          })
      })
    }
    return existingPromise
  }

  const { idempotencyKey, ...requestOptions } = options
  const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData
  const method = String(requestOptions.method || 'GET').toUpperCase()
  const requestId = createRequestId()
  const omitAuthContext =
    endpoint === '/auth/login' ||
    endpoint === '/auth/magic-link/request' ||
    endpoint === '/auth/magic-link/verify' ||
    endpoint === '/auth/join/verify' ||
    endpoint === '/auth/join/complete'
  const config = {
    ...requestOptions,
    headers: {
      ...(!isFormData ? { 'Content-Type': 'application/json' } : {}),
      ...(token && !omitAuthContext && { Authorization: `Bearer ${token}` }),
      ...(tenantSlug && !omitAuthContext && { 'X-Tenant': tenantSlug }),
      'X-Request-Id': requestId,
      ...(isMutationMethod(method) && !isFormData && idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {}),
      ...requestOptions.headers,
    },
  }

  const requestPromise = (async () => {
    try {
      if (options.signal?.aborted) {
        throw new DOMException('Aborted', 'AbortError')
      }

      const response = await fetch(url, config)

      if (response.status === 401) {
        let payload = null
        try {
          payload = await response.json()
        } catch {
          payload = null
        }
        const errorCode = payload?.code || 'UNAUTHORIZED'
        if (errorCode === 'impersonation_session_expired') {
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('socialbox:impersonation-expired'))
          }
        } else {
          const adminBackup = typeof window !== 'undefined' ? readAdminTokenBackup() : null
          if (adminBackup) {
            localStorage.setItem('auth_token', adminBackup)
            try {
              sessionStorage.removeItem(ADMIN_TOKEN_BACKUP_KEY)
            } catch { /* ignore */ }
            if (typeof window !== 'undefined') {
              window.dispatchEvent(new CustomEvent('socialbox:impersonation-expired'))
            }
          } else {
            setAuthToken(null)
          }
        }
        const authError = new Error(payload?.error || 'Unauthorized')
        authError.status = 401
        authError.code = errorCode
        authError.endpoint = endpoint
        throw authError
      }

      if (response.status === 204) {
        return undefined
      }

      let data
      try {
        data = await response.json()
      } catch {
        throw new Error(response.ok ? 'Invalid response from server' : `Server error (${response.status}). The API may be misconfigured or down.`)
      }

      if (!response.ok) {
        const rawError = data.error || `HTTP error! status: ${response.status}`
        const userMessage = getUserFriendlyApiErrorMessage({
          endpoint,
          status: response.status,
          rawMessage: rawError,
          code: data.code,
        })
        const httpError = new Error(userMessage)
        httpError.status = response.status
        httpError.endpoint = endpoint
        httpError.rawMessage = rawError
        httpError.requestId = response.headers.get('x-request-id') || requestId
        if (data.code) httpError.code = data.code
        if (data.code === 'impersonation_session_expired' && typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('socialbox:impersonation-expired'))
        }
        if (response.status === 409 && endpoint === '/influencers/roster') {
          httpError.code = 'INFLUENCER_ROSTER_DUPLICATE'
        }
        throw httpError
      }

      return data
    } catch (error) {
      if (!isAbortError(error) && !isExpectedLoginFailure(endpoint, error)) {
        console.error('API request error:', error)
        const msg = error?.message || ''
        if (msg === 'Failed to fetch' || msg.includes('NetworkError') || msg.includes('Load failed')) {
          throw new Error(getApiConnectionErrorMessage())
        }
      }
      throw error
    } finally {
      pendingRequests.delete(cacheKey)
    }
  })()

  pendingRequests.set(cacheKey, requestPromise)
  return requestPromise
}

export { API_URL }
